import {
  IconResponse,
  PrimaryCategoryChildrenListResponse,
  type PrimaryCategoryParentListResponse,
  PrimaryCategoryParentResponse,
  SecondaryCategoryParentResponse,
  TertiaryCategoryParentListResponse,
  TertiaryCategoryParentResponse,
} from '@middleware';
import {type PrimaryCategory, type SecondaryCategory, type TertiaryCategory} from '@models/category';
import {cloneDeep, omit} from 'lodash';

/**
 * Returns a copy of the primary categories list in the given
 * PrimaryCategoryChildrenListResponse object, with the `parent_id`
 * and `children` properties populated for each item in the
 * tree of categories.
 */
export const addChildrenToCats = (pcplResponse: PrimaryCategoryParentListResponse): PrimaryCategory[] => {
  const primaryCats = cloneDeep(pcplResponse.primary_categories) as PrimaryCategory[];

  for (const primaryCat of primaryCats) {
    const secondaryCats = primaryCat.secondary_categories as SecondaryCategory[];
    primaryCat.children = secondaryCats;

    for (const secondaryCat of secondaryCats) {
      const tertiaryCats = secondaryCat.tertiary_categories as TertiaryCategory[];
      secondaryCat.children = tertiaryCats;
      secondaryCat.parent_id = primaryCat.id;

      for (const tertiaryCat of tertiaryCats) {
        tertiaryCat.children = null;
        tertiaryCat.parent_id = secondaryCat.id;
      }
    }
  }

  return primaryCats;
};

/**
 * Returns a fully-populated TertiaryCategoryParentListResponse object
 * for the given PrimaryCategoryChildrenListResponse object.
 */
export const convertToCategoryParentListResponse = (
  primaryCategories: PrimaryCategoryChildrenListResponse,
): TertiaryCategoryParentListResponse => {
  const result: TertiaryCategoryParentListResponse = {tertiary_categories: []};

  for (const primaryCR of primaryCategories.primary_categories) {
    const primaryPR = omit(primaryCR, ['secondary_categories']) as PrimaryCategoryParentResponse;

    for (const secondaryCR of primaryCR.secondary_categories) {
      const secondaryPR: SecondaryCategoryParentResponse = {
        ...omit(secondaryCR, ['primary_category_id', 'tertiary_categories']),
        primary_category: primaryPR,
        icon: omit(secondaryCR.icon, 'icon_type_id') as IconResponse,
      };

      for (const tertiaryCR of secondaryCR.tertiary_categories) {
        result.tertiary_categories.push({
          ...omit(tertiaryCR, ['secondary_category_id']),
          secondary_category: secondaryPR,
        } as TertiaryCategoryParentResponse);
      }
    }
  }

  return result;
};
